import { z } from 'zod';
import { DEVICE_INVENTORY_PROGRESS_KINDS } from '@/features/it-operation-core';
import { DEVICE_INVENTORY_GENRES, DEVICE_INVENTORY_REPORT_RESULTS, DEVICE_INVENTORY_SELF_REPORT_ORDER_FIELDS, DEVICE_INVENTORY_TABS } from '@/lib/api/orpc/contracts/it-operation-core-device-inventory';

/**
 * 束ね画面のタブ。`count-device-inventory-instances-by-tag-tabs` のレスポンスキーと 1:1。
 * 並び順は画面の左→右（依頼前 → 完了 → すべて）。
 *
 * `all` だけが画面固有。一覧 API の `tab` は 5 値で、「すべて」は
 * **パラメータを送らないこと**で表す（既定値を URL に書き戻さない流儀とも揃う）。
 */
export const DEVICE_INVENTORY_CONFIRMATION_TABS = [...DEVICE_INVENTORY_TABS, 'all'] as const;

/** 束ね画面の既定ページサイズ。この画面へ navigate する側（一覧・起動フロー）と共有する */
export const DEVICE_INVENTORY_CONFIRMATION_DEFAULT_PAGE_SIZE = 100;
export type DeviceInventoryConfirmationTab = (typeof DEVICE_INVENTORY_CONFIRMATION_TABS)[number];

/**
 * **起動フロー直後だけ**の着地タブ（`DeviceInventoryAnchorDate` から送る）。
 *
 * 依頼前にするのは、起動直後にすべき操作が「依頼」だから。一括依頼を実行すると
 * 行が抜けて件数が減るので、実行できたことも分かる（「すべて」タブだとバッジが
 * 変わるだけで伝わらない）。空タブにはならない: 起動直後は BE の
 * `classifyDeviceInventoryTab` が依頼タスク未完了の Instance を `notRequested` へ
 * 倒すため、対象デバイス全件がこのタブに入る。
 *
 * 🔴 **束ね一覧の「確認画面へ」からは送らない**（`DeviceInventoryOperation`）。
 * あちらは進行中・完了の束ねを俯瞰する導線で、依頼を済ませた束ねを開くと依頼前タブが
 * 0 件になる。行のサマリは `completedCount` / `totalCount` しか持たず、タブ別件数は
 * 別 API なので遷移前に「空になるか」を判定できない。入口ごとに意味が違うので定数を
 * 共有せず、あちらは既定（`tab` 省略 = すべて）に任せる。名前を `AFTER_START` に
 * しているのは、他の入口から流用されないようにするため。
 *
 * 🔴 これは**入口が明示的に送る値**であって、検索スキーマの既定値ではない。
 * `tab` 省略の意味は「すべて」のまま変えないこと（既定値を URL に書き戻さない流儀。
 * 既定を依頼前にすると `?tab=` の無い URL すべての意味が変わる）。
 */
export const DEVICE_INVENTORY_CONFIRMATION_TAB_AFTER_START = 'notRequested' as const satisfies DeviceInventoryConfirmationTab;

/**
 * 束ね画面の検索パラメータ。
 *
 * `packageId` / `patternId` はパンくずと「一覧へ戻る」導線のためだけに持つ
 * （束ねの母集団は `tagId` だけで決まる。BE も patternId を where に使っていない）。
 *
 * `tab` は `undefined` を「すべて」として扱う（年末調整の `documentTab` と同じ流儀。
 * 既定値を URL に書き戻さないため）。
 *
 * `progressKinds` は**画面語彙の 4 値**（Figma のバッジと同じ）。一覧 API へは
 * `displayStatuses`（7 値）へ展開して送る（`toDeviceInventoryDisplayStatuses`）。
 * URL に 4 値で持つことで短く読める形にしている。
 */
export const DeviceInventoryConfirmationSearchSchema = z.object({
  packageId: z.string().min(1),
  patternId: z.string().min(1),
  tagId: z.string().min(1),
  tab: z.enum(DEVICE_INVENTORY_CONFIRMATION_TABS).optional().catch(undefined),
  pageIndex: z.number().int().nonnegative().catch(0),
  pageSize: z.number().int().positive().max(2000).catch(DEVICE_INVENTORY_CONFIRMATION_DEFAULT_PAGE_SIZE),
  orderBy: z.enum(DEVICE_INVENTORY_SELF_REPORT_ORDER_FIELDS).optional().catch(undefined),
  orderDirection: z.enum(['asc', 'desc']).optional().catch(undefined),
  freeText: z.string().max(100).optional().catch(undefined),
  deviceGenres: z.array(z.enum(DEVICE_INVENTORY_GENRES)).optional().catch(undefined),
  progressKinds: z.array(z.enum(DEVICE_INVENTORY_PROGRESS_KINDS)).optional().catch(undefined),
  reportResults: z.array(z.enum(DEVICE_INVENTORY_REPORT_RESULTS)).optional().catch(undefined)
});